import {
  computeAddress,
  encodeBase64,
  getAddress,
  getBytes,
  hashMessage,
  hexlify,
  N,
  Signature,
  SigningKey,
  toBeHex,
} from "ethers";

/**
 * EIP-191 personal_sign, not ADR-36 or a transaction signature. Freeze this
 * low-S, 64-byte r || s base64 representation: private-account derivation uses
 * the returned string. Recovery-bit encodings must not create new identities.
 */
export const verifiedPersonalSignature = (
  message: string,
  response: unknown,
  expectedAddress: string
) => {
  let signature: Signature;
  let publicKey: string;
  try {
    if (
      typeof response !== "string" ||
      !/^0x(?:[0-9a-fA-F]{128}|[0-9a-fA-F]{130})$/.test(response)
    )
      throw new Error();

    const bytes = getBytes(response);
    const r = BigInt(hexlify(bytes.slice(0, 32)));
    let s = BigInt(hexlify(bytes.slice(32, 64)));
    let yParity: 0 | 1;
    if (bytes.length === 64) {
      // EIP-2098 packs the recovery bit into the highest bit of s.
      yParity = s >> 255n === 1n ? 1 : 0;
      s &= (1n << 255n) - 1n;
    } else {
      const v = bytes[64];
      if (![0, 1, 27, 28].includes(v)) throw new Error();
      yParity = v === 1 || v === 28 ? 1 : 0;
    }
    if (r <= 0n || r >= N || s <= 0n || s >= N) throw new Error();
    if (s > N / 2n) {
      s = N - s;
      yParity = yParity === 0 ? 1 : 0;
    }
    signature = Signature.from({
      r: toBeHex(r, 32),
      s: toBeHex(s, 32),
      yParity,
    });
    publicKey = SigningKey.recoverPublicKey(hashMessage(message), signature);
  } catch {
    // Ethers errors may contain the signature. Do not expose it in UI/logs:
    // this signature is private-account key material, not a public transaction.
    throw new Error("The wallet returned an invalid message signature");
  }

  if (computeAddress(publicKey) !== getAddress(expectedAddress))
    throw new Error("The wallet signed with a different account");

  return {
    pub_key: {
      type: "os/PubKeyEthSecp256k1",
      value: encodeBase64(SigningKey.computePublicKey(publicKey, true)),
    },
    signature: encodeBase64(`0x${signature.r.slice(2)}${signature.s.slice(2)}`),
  };
};
